/**
 * The conversions every colour instrument in the app needs, in one place.
 *
 * `ColorPicker` is a set of PAINTED SURFACES: a saturation square, a hue strip, an opacity strip. It
 * takes numbers and hands back numbers, and it deliberately knows nothing about hex — which is right,
 * and which leaves every consumer to carry the same forty lines of HSV maths. `ColorWheel` had them
 * privately, and the preview's text popover was about to be the second copy. Two copies of a
 * conversion is how two instruments start disagreeing about what `#7b00ff` is.
 *
 * SO THIS IS A LEAF, on purpose. No React, no styles, no imports. Anything that draws a colour can
 * take it without taking the instrument it was extracted from — the same cut `textScale.ts` makes for
 * the font ladder, and for the same reason.
 *
 * HSV rather than HSL because that is what a saturation-square picker is: the square's two axes ARE
 * s and v, so the picker's own coordinates need no conversion at all and only the round trip to hex
 * does.
 */

/** Hue in degrees `[0, 360)`, saturation and value in `[0, 1]`. The picker's own coordinate space. */
export type Hsv = [h: number, s: number, v: number];

/** `#rrggbb`, always six digits and always lowercase. */
export function hsvToHex(h: number, s: number, v: number): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const toHex = (val: number) =>
    Math.round(val * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
}

/**
 * The inverse, with one asymmetry worth stating: HUE IS NOT RECOVERABLE FROM A GREY.
 *
 * Black, white and every grey have zero saturation, and every hue produces them — so this returns 0,
 * which is red. A picker that stores only the hex therefore loses the hue the moment the user drags
 * to the bottom of the square, and the strip jumps back to red under their hand. That is why the
 * instruments hold HSV as their own state and convert OUT rather than round-tripping through hex on
 * every move.
 */
export function hexToHsv(hex: string): Hsv {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

/** The fully saturated colour at this hue — what the saturation square is painted over. */
export function hueToColor(h: number): string {
  return hsvToHex(h, 1, 1);
}

/**
 * A typed hex fragment as a `#rrggbb` string, or null if it is not one yet.
 *
 * Null is the ANSWER for a half-typed field, not a failure: someone typing `#ff` is two keystrokes
 * from a colour, and a field that rejected or corrected them mid-word would be unusable. Callers
 * hold the draft and commit on the first value that parses.
 */
export function normalizeHex(raw: string): string | null {
  const v = raw.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(v))
    return `#${v
      .split('')
      .map((c) => c + c)
      .join('')
      .toLowerCase()}`;
  return null;
}

/**
 * A colour and its alpha, back out as the shortest CSS that carries both.
 *
 * Hex when it is opaque, `rgba()` when it is not — because `#rrggbb` cannot hold an alpha and
 * `#rrggbbaa` is not safe to write into an arbitrary document (it is well supported now, but the
 * documents this writes into are model-authored and rendered in whatever the reader opens them in,
 * and `rgba()` has never not worked).
 *
 * THE POINT OF IT is that a picker which only ever emitted hex would silently make transparent text
 * opaque. Artifact body copy at `rgba(253, 252, 252, 0.6)` is ordinary, and dragging the colour a
 * shade warmer should not also turn the alpha off.
 */
export function toCssColor(hex: string, alpha: number): string {
  if (!(alpha < 1)) return hex;
  const channel = (at: number) => parseInt(hex.slice(at, at + 2), 16) || 0;
  // Three places is where `getComputedStyle` rounds too, so a value read out and written straight back
  // is byte-identical and the funnel treats it as the no-op it is.
  const a = Math.max(0, Math.round(alpha * 1000) / 1000);
  return `rgba(${channel(1)}, ${channel(3)}, ${channel(5)}, ${a})`;
}

/**
 * ANY CSS colour a document can hand back, reduced to `#rrggbb`.
 *
 * This exists because of where the colours come from. `getComputedStyle().color` is always an
 * `rgb()` or `rgba()` string — never the hex the author wrote — so an instrument that only read hex
 * would open on black every time it was pointed at real content.
 *
 * Alpha is READ and returned but is not part of the hex, because the two are edited separately
 * everywhere in this app. A caller with no opacity control can ignore it; one that has an opacity
 * field gets the starting value for free.
 *
 * Unparseable input answers black at full alpha rather than throwing. The callers are pickers, and a
 * picker that crashes on an unusual colour is worse than one that opens somewhere neutral.
 */
export function parseCssColor(input: string): { hex: string; alpha: number } {
  const value = (input ?? '').trim();
  if (value === '' || value === 'transparent') return { hex: '#000000', alpha: 0 };

  // The leading `-?` on each channel is not defensive noise: `rgb()` accepts out-of-range values and
  // clamps them, so a document may legally carry one. A regex that rejected it would drop the whole
  // colour and answer black — silently turning a nearly-red heading into a black one in the picker.
  const rgb = value.match(
    /^rgba?\(\s*(-?[\d.]+)[\s,]+(-?[\d.]+)[\s,]+(-?[\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/i,
  );
  if (rgb) {
    const channel = (raw: string) =>
      Math.max(0, Math.min(255, Math.round(Number(raw) || 0)))
        .toString(16)
        .padStart(2, '0');
    const rawAlpha = rgb[4];
    const alpha =
      rawAlpha === undefined
        ? 1
        : rawAlpha.endsWith('%')
          ? (Number(rawAlpha.slice(0, -1)) || 0) / 100
          : Number(rawAlpha) || 0;
    return {
      hex: `#${channel(rgb[1])}${channel(rgb[2])}${channel(rgb[3])}`,
      alpha: Math.max(0, Math.min(1, alpha)),
    };
  }

  // `#rgb`, `#rrggbb`, and `#rrggbbaa` — the last one carries its alpha in the string, so it is
  // split off here rather than being silently painted as part of the colour.
  const hash = value.match(/^#([0-9a-fA-F]{3,8})$/);
  if (hash) {
    const digits = hash[1];
    if (digits.length === 8) {
      const hex = normalizeHex(digits.slice(0, 6));
      if (hex) return { hex, alpha: parseInt(digits.slice(6, 8), 16) / 255 };
    }
    const hex = normalizeHex(digits);
    if (hex) return { hex, alpha: 1 };
  }

  // Named colours, `hsl()`, `color()`, `oklch()` and the rest. They are legal in a document and this
  // does not read them; the picker opens on black and the user's first drag replaces it. Worth
  // knowing rather than worth a parser — the values this is actually fed come from
  // `getComputedStyle`, which normalises all of them to `rgb()`.
  return { hex: '#000000', alpha: 1 };
}

/**
 * A colour as HEX, for display only (Beka, 2026-09-25: the Properties panel reads hex, not rgb): `#rrggbb`,
 * or `#rrggbbaa` when it is not opaque. What gets SAVED stays `toRgbColor`'s `rgb()`, because a hand value
 * cannot carry a `#`; this is only what the user reads and types.
 *
 * LOWERCASE, EXACTLY AS DIALKIT'S PICKER WRITES IT, and that is load-bearing. The picker compares the value
 * it is handed with the one it last sent, and any difference reads as a colour from outside: it re-reads its
 * OKLCH colour from the 8-bit hex. Mid-drag, that snaps the hue of a grey or a near-white to noise and the
 * handle stops following the pointer. A value that round-trips byte for byte is never re-read.
 *
 * Read as strictly as `toRgbColor` reads, and for the same reason turned the other way: anything that
 * cannot be converted exactly (a name, `hsl()`, `var()`) comes back unchanged rather than as black.
 */
export function toHexColor(input: string): string {
  const rgb = typeof input === 'string' ? toRgbColor(input) : null;
  if (!rgb) return input;
  const { hex, alpha } = parseCssColor(rgb);
  const tail = alpha < 1 ? Math.round(alpha * 255).toString(16).padStart(2, '0') : '';
  return `${hex}${tail}`.toLowerCase();
}

/**
 * A colour a DOCUMENT may be handed, as `rgb(r, g, b)` or `rgba(r, g, b, a)` — or null.
 *
 * The other half of `toCssColor`, and the opposite temperament to `parseCssColor`. That one never
 * fails because its callers are pickers that must open somewhere; this one fails loudly because its
 * callers are WRITES. The hand-edit funnel (`isHandValue` in `birtvyOpen/artifactHand.ts`) refuses a
 * `#` outright — the validator regexes the whole document, attributes included — so a hex from a
 * colour control has to become `rgb()` before it is previewed or committed, and anything that cannot
 * be converted exactly has to stop here rather than arrive as something else.
 *
 * STRICT, AND NEVER BLACK. It reads the shapes a colour control or a computed style actually emits:
 * `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb()` / `rgba()` in the comma form or the space form with
 * an optional `/ alpha` (a number or a percentage), and `transparent`. Everything else — a name,
 * `hsl()`, `oklch()`, `var()`, a half-typed field, a mixed comma-and-slash form the engine would
 * itself reject — answers null. `parseCssColor`'s "open on black" is the right fallback for a picker
 * and the wrong one for a write: it would paint a card black because its colour was spelled in a way
 * this file does not read.
 *
 * The output is the shape `getComputedStyle` answers with — `rgb(17, 17, 17)`, alpha rounded to three
 * places with no trailing zeros — so a value read out of the document and written straight back is
 * byte-identical, and the funnel treats it as the no-op it is. Channels round to integers and clamp to
 * 0-255, alpha clamps to 0-1, and an alpha that rounds to 1 drops the `a` rather than writing `, 1`.
 */
export function toRgbColor(input: string): string | null {
  if (typeof input !== 'string') return null;
  const value = input.trim();
  if (value === '') return null;
  if (value.toLowerCase() === 'transparent') return 'rgba(0, 0, 0, 0)';

  const hash = value.match(/^#([0-9a-fA-F]{3,8})$/);
  if (hash) {
    const digits = hash[1];
    // Four and eight are the forms with an alpha digit; five and seven are not colours at all.
    if (digits.length !== 3 && digits.length !== 4 && digits.length !== 6 && digits.length !== 8) return null;
    const full = digits.length <= 4 ? digits.replace(/./g, (c) => c + c) : digits;
    const byte = (at: number) => parseInt(full.slice(at, at + 2), 16);
    return formatRgb(byte(0), byte(2), byte(4), full.length === 8 ? byte(6) / 255 : 1);
  }

  const fn = value.match(/^rgba?\((.*)\)$/i);
  if (!fn) return null;
  const body = fn[1].trim();

  // THE TWO SYNTAXES DO NOT MIX. `rgb(1, 2, 3 / 0.5)` and `rgb(1 2 3 0.5)` are both invalid CSS, and
  // an engine handed either drops the declaration — so a value that "mostly parses" here would be one
  // this function promised was safe and the document then ignored.
  let channels: string[];
  let alpha: string | undefined;
  if (body.includes(',')) {
    if (body.includes('/')) return null;
    const parts = body.split(',').map((part) => part.trim());
    if (parts.length !== 3 && parts.length !== 4) return null;
    channels = parts.slice(0, 3);
    // The comma form is also all-numbers or all-percentages; `rgb(100%, 0, 0)` is dropped by the
    // engine for the same reason as the mixed separators above.
    if (new Set(channels.map((c) => c.endsWith('%'))).size > 1) return null;
    alpha = parts[3];
  } else {
    const [colour, rest, ...extra] = body.split('/').map((part) => part.trim());
    if (extra.length > 0 || rest === '') return null;
    channels = colour.split(/\s+/);
    if (channels.length !== 3) return null;
    alpha = rest;
  }

  const [r, g, b] = channels.map((raw) => cssNumber(raw, 255));
  const a = alpha === undefined ? 1 : cssNumber(alpha, 1);
  if (r === null || g === null || b === null || a === null) return null;
  return formatRgb(r, g, b, a);
}

/**
 * One CSS `<number>` or `<percentage>`, with the percentage scaled against `full` — 255 for a
 * channel, 1 for an alpha. Null for anything else, including `none`, `calc()` and a bare `.`, so a
 * token this does not understand can never quietly become 0.
 */
function cssNumber(raw: string, full: number): number | null {
  const m = raw.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?)(%?)$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (Number.isNaN(n)) return null;
  return m[2] === '%' ? (n / 100) * full : n;
}

/** The computed-style spelling: integer channels in 0-255, alpha in 0-1 at three places, `rgb` when opaque. */
function formatRgb(r: number, g: number, b: number, alpha: number): string {
  const channel = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  // `+ 0` folds a rounded `-0` into `0`, so a hair-negative alpha never prints as `-0`.
  const a = Math.max(0, Math.min(1, Math.round(alpha * 1000) / 1000)) + 0;
  const rgb = `${channel(r)}, ${channel(g)}, ${channel(b)}`;
  return a === 1 ? `rgb(${rgb})` : `rgba(${rgb}, ${a})`;
}
